import type { Observable } from 'rxjs';

import type { RtComponentOutletConfig, StaticInputsComponent } from '@recruitee/common';

export interface DataTableColumnOptions {
  draggable: boolean;
  resizable: boolean;
  removable: boolean;
  sortable: boolean;
}

export type TableColumnAutoFit = 'width' | 'minWidth' | 'maxWidth';

export interface DataTableColumnGeometry {
  width: number | 'auto';
  minWidth?: number;
  maxWidth?: number;
  autoFitProp?: TableColumnAutoFit;
}

export interface DataTableColumnGeometryChange {
  autofit: boolean;
  columns: Array<{
    id: string;
    geometry: DataTableColumnGeometry;
  }>;
}

export interface DataTableRow {
  id: number | string;
  private?: boolean;
  disabled?: boolean;
}

export interface DataTableSelectionCellContext<R extends DataTableRow = DataTableRow> {
  row: R;
  selected: boolean;
  handleSelect: (selected: boolean) => void;
}

export interface DataTableCellContext<R extends DataTableRow = DataTableRow> {
  columnId: number | string;
  row: R;
  rowEven: boolean;
  rowIndex: number;
  cellIndex: number;
}

export interface DataTableColumn<Id = string> {
  id: Id;
  label?: string;
  sort?: 'asc' | 'desc' | null;
  geometry: DataTableColumnGeometry;
  options: DataTableColumnOptions;
  visible?: boolean;
  index?: number;
  isNew?: boolean;
}

export interface DataTableColumnsCache {
  [key: string]: { index: number; visible: boolean };
}

export interface DataTableColumnWithVisibility extends DataTableColumn<any> {
  visible: boolean;
}

export type TableCellFactoryResult<Cmp extends StaticInputsComponent> =
  | RtComponentOutletConfig<Cmp>
  | Observable<RtComponentOutletConfig<Cmp>>;

export type TableCellFactory<
  Row extends DataTableRow,
  Cmp extends StaticInputsComponent = StaticInputsComponent,
> = (row: DataTableCellContext<Row>) => TableCellFactoryResult<Cmp>;

export interface NewDataTableColumn<Row extends DataTableRow, Id = string>
  extends DataTableColumn<Id> {
  componentFactory: TableCellFactory<Row>;
}

/*
 * Called in injection context once for every provided column in the table.
 */
export type ColumnFactory<Row extends DataTableRow> = () => NewDataTableColumn<Row>;

export const defineTableColumn = <Row extends DataTableRow>(columnFactory: ColumnFactory<Row>) =>
  columnFactory;
